import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import {
  Button,
  Combobox,
  DataGrid,
  Modal,
  TextField
} from '../../design-system';
import {
  addWell,
  downloadScalExcelTemplate,
  importScalExcelBatch,
  saveImportedScalSamples,
  ScalExcelTemplateFormat
} from '../../api/data';
import {
  BatchImportFileResult,
  BatchImportParseResponseContent,
  ImportFileStatus,
  ParsedScalSample
} from '../../@types/import/types';
import { Sample } from '../../@types/samples/types';
import { Well } from '../../@types/wells/types';
import styles from './ScalExcelImportModal.module.css';

type IconProps = { size?: number; color?: string };
const PlusIcon = FiPlus as React.FC<IconProps>;
const XIcon = FiX as React.FC<IconProps>;

type Props = {
  open: boolean;
  onClose: () => void;
  projectUid: string | null;
  wells: Well[];
  defaultWellUid?: string | null;
  onWellsChange?: (wells: Well[]) => void;
  onImported: (
    savedSamples: Sample[],
    targetWellUid: string
  ) => Promise<void> | void;
};

type PreviewSampleRow = {
  id: string;
  fileIndex: number;
  sampleIndex: number;
  filename: string;
  sampleCode: string;
  depth: string;
  porosity: string;
  permeability: string;
  curves: string[];
  sectionsDetected: string[];
  missingOptionalSections: string[];
  missingRequiredFields: string[];
  status: string;
};

const SAMPLE_CODE_MAX_LENGTH = 50;
const SECTION_LABELS = [
  'Sample Information',
  'Capillary Pressure',
  'Water-Oil Relative Permeability',
  'Gas-Oil Relative Permeability'
];

const EMPTY_NEW_WELL = {
  name: '',
  color: '#4ADE80'
};

const isExcelFile = (file: File) => /\.(xls|xlsx)$/i.test(file.name);

const formatNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? String(numeric) : '—';
};

const formatEditableNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? String(numeric) : '';
};

const statusLabel = (status: ImportFileStatus | string) =>
  status.replace('_', ' ');

const curveLabel = (key: string) => {
  if (key === 'pc_model') return 'Capillary pressure';
  if (key === 'water_oil') return 'Water-oil relative permeability';
  if (key === 'gas_oil') return 'Gas-oil relative permeability';
  return key;
};

const statusClass = (status: string) => {
  if (status === 'success' || status === 'valid') return styles.badgeSuccess;
  if (status === 'partial_success' || status === 'partial')
    return styles.badgePartial;
  if (status === 'failed' || status === 'invalid') return styles.badgeInvalid;
  return styles.badgeNeutral;
};

const collectMessages = (
  files: BatchImportFileResult[],
  key: 'warnings' | 'errors'
) =>
  files.flatMap(file => [
    ...file[key].map(message => `${file.filename}: ${message}`),
    ...(file.parsed_data.samples ?? []).flatMap(sample =>
      (sample.diagnostics?.[key] ?? []).map(
        message => `${file.filename} / ${sample.sample_code}: ${message}`
      )
    )
  ]);

const sampleCurves = (sample: ParsedScalSample) => {
  const diagnosticCurves = sample.diagnostics?.curves_found ?? [];
  const payloadCurves = Object.entries(sample.input_sample_curves ?? {})
    .filter(([, value]) => value && Object.values(value).some(Boolean))
    .map(([key]) => key);
  return Array.from(new Set([...diagnosticCurves, ...payloadCurves])).map(
    curveLabel
  );
};

const sampleSections = (sample: ParsedScalSample) => {
  const diagnosticSections = sample.diagnostics?.sections_detected ?? [];
  const sections = new Set(diagnosticSections);
  if (
    !sections.size &&
    (sample.sample_code || sample.depth !== null || sample.input_sample_values)
  ) {
    sections.add('Sample Information');
  }
  if (sample.input_sample_curves?.pc_model) sections.add('Capillary Pressure');
  if (sample.input_sample_curves?.water_oil)
    sections.add('Water-Oil Relative Permeability');
  if (sample.input_sample_curves?.gas_oil)
    sections.add('Gas-Oil Relative Permeability');
  return Array.from(sections);
};

const flattenSamples = (
  parsed: BatchImportParseResponseContent | null
): PreviewSampleRow[] => {
  if (!parsed) return [];
  return parsed.files.flatMap((file, fileIndex) =>
    (file.parsed_data.samples ?? []).map((sample, index) => ({
      id: `${file.filename}-${fileIndex}-${index}`,
      fileIndex,
      sampleIndex: index,
      filename: file.filename,
      sampleCode: sample.sample_code ?? '',
      depth: formatEditableNumber(sample.depth),
      porosity: formatEditableNumber(sample.input_sample_values?.porosity),
      permeability: formatEditableNumber(
        sample.input_sample_values?.permeability
      ),
      curves: sampleCurves(sample),
      sectionsDetected: sampleSections(sample),
      missingOptionalSections:
        sample.diagnostics?.missing_optional_sections ?? [],
      missingRequiredFields: sample.diagnostics?.missing_required_fields ?? [],
      status: sample.diagnostics?.status ?? 'partial'
    }))
  );
};

const isInvalidSample = (sample: ParsedScalSample) =>
  sample.diagnostics?.status === 'invalid';

const sampleIsImportable = (sample: ParsedScalSample) =>
  !isInvalidSample(sample);

const sampleCodeError = (sampleCode: string) => {
  const trimmed = sampleCode.trim();
  if (!trimmed) return 'Sample code is required.';
  if (trimmed.length > SAMPLE_CODE_MAX_LENGTH) {
    return `Sample code must be ${SAMPLE_CODE_MAX_LENGTH} characters or fewer.`;
  }
  return null;
};

const numericFieldError = (label: string, value: string) => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '—') return `${label} is required.`;
  const numeric = Number(trimmed);
  if (!Number.isFinite(numeric)) return `${label} must be a valid number.`;
  return null;
};

const sampleRequiredErrors = (sample: ParsedScalSample) => {
  const errors: string[] = [];
  if (sampleCodeError(sample.sample_code ?? '')) errors.push('sample_code');
  if (numericFieldError('Depth', formatNumber(sample.depth)))
    errors.push('depth');
  if (
    numericFieldError(
      'Porosity',
      formatNumber(sample.input_sample_values?.porosity)
    )
  )
    errors.push('porosity');
  if (
    numericFieldError(
      'Permeability',
      formatNumber(sample.input_sample_values?.permeability)
    )
  )
    errors.push('permeability');
  return errors;
};

const countImportableSamples = (files: BatchImportFileResult[]) =>
  files.reduce(
    (total, file) =>
      total +
      (file.parsed_data.samples ?? []).filter(sampleIsImportable).length,
    0
  );

const parseNumericInput = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '—') return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
};

const requiredStatusForSample = (sample: ParsedScalSample) => {
  const missingRequiredFields = sampleRequiredErrors(sample);
  const diagnostics = sample.diagnostics ?? {};
  const nonRequiredErrors = (diagnostics.errors ?? []).filter(
    message => !message.toLowerCase().includes('missing required fields')
  );
  const isValidForImport =
    missingRequiredFields.length === 0 && nonRequiredErrors.length === 0;

  return {
    diagnostics: {
      ...diagnostics,
      status: isValidForImport
        ? diagnostics.missing_optional_sections?.length
          ? 'partial'
          : 'valid'
        : 'invalid',
      missing_required_fields: missingRequiredFields,
      is_valid_for_import: isValidForImport,
      errors: [
        ...nonRequiredErrors,
        ...(missingRequiredFields.length > 0
          ? [`Missing required fields: ${missingRequiredFields.join(', ')}`]
          : [])
      ]
    }
  } satisfies Pick<ParsedScalSample, 'diagnostics'>;
};

const ScalExcelImportModal = ({
  open,
  onClose,
  projectUid,
  wells,
  defaultWellUid,
  onWellsChange,
  onImported
}: Props) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const [parsedPayload, setParsedPayload] =
    useState<BatchImportParseResponseContent | null>(null);
  const [availableWells, setAvailableWells] = useState<Well[]>(wells);
  const [targetWellUid, setTargetWellUid] = useState(defaultWellUid ?? '');
  const [isCreatingNewWell, setIsCreatingNewWell] = useState(false);
  const [newWellData, setNewWellData] = useState(EMPTY_NEW_WELL);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] =
    useState<ScalExcelTemplateFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const resetImportState = useCallback(() => {
    setSelectedFiles([]);
    setValidationMessage(null);
    setParsedPayload(null);
    setIsCreatingNewWell(false);
    setNewWellData(EMPTY_NEW_WELL);
    setParsing(false);
    setSaving(false);
    setDownloadingTemplate(null);
    setError(null);
    setSuccess(null);
  }, []);

  useEffect(() => {
    if (!open) {
      resetImportState();
      setAvailableWells(wells);
      setTargetWellUid(defaultWellUid ?? '');
    }
  }, [open, defaultWellUid, wells, resetImportState]);

  useEffect(() => {
    if (open) {
      setAvailableWells(wells);
      setTargetWellUid(defaultWellUid ?? '');
    }
  }, [open, defaultWellUid, wells]);

  const warningMessages = useMemo(
    () => collectMessages(parsedPayload?.files ?? [], 'warnings'),
    [parsedPayload]
  );
  const errorMessages = useMemo(
    () => collectMessages(parsedPayload?.files ?? [], 'errors'),
    [parsedPayload]
  );
  const sampleRows = useMemo(
    () => flattenSamples(parsedPayload),
    [parsedPayload]
  );
  const hasInvalidSamples = sampleRows.some(row => row.status === 'invalid');
  const sampleCodeErrors = sampleRows
    .map(row => sampleCodeError(row.sampleCode))
    .filter(Boolean);
  const requiredValueErrors = sampleRows.flatMap(row => {
    return [
      numericFieldError('Depth', row.depth),
      numericFieldError('Porosity', row.porosity),
      numericFieldError('Permeability', row.permeability)
    ].filter(Boolean);
  });
  const hasTargetWell = Boolean(
    targetWellUid || (isCreatingNewWell && newWellData.name.trim())
  );
  const wellOptions = availableWells.map(well => ({
    value: well.uid,
    label: well.well_name
  }));

  const canParse = selectedFiles.length > 0 && !parsing && !saving;
  const canSave = Boolean(
    parsedPayload &&
      parsedPayload.summary.samples_valid > 0 &&
      !hasInvalidSamples &&
      sampleCodeErrors.length === 0 &&
      requiredValueErrors.length === 0 &&
      hasTargetWell &&
      projectUid &&
      !saving &&
      !parsing
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const validFiles = files.filter(isExcelFile);
    const invalidFiles = files.filter(file => !isExcelFile(file));
    resetImportState();
    setSelectedFiles(validFiles);
    setValidationMessage(
      invalidFiles.length > 0
        ? `Unsupported file type: ${invalidFiles.map(file => file.name).join(', ')}. Only .xls and .xlsx files are supported.`
        : null
    );
  };

  const handleParse = async () => {
    if (!canParse) return;
    setParsing(true);
    setError(null);
    setSuccess(null);
    try {
      const parsed = await importScalExcelBatch(selectedFiles);
      setParsedPayload(parsed);
    } catch (err) {
      setParsedPayload(null);
      setError(
        err instanceof Error ? err.message : 'Failed to parse SCAL Excel files.'
      );
    } finally {
      setParsing(false);
    }
  };

  const handleDownloadTemplate = async (format: ScalExcelTemplateFormat) => {
    setDownloadingTemplate(format);
    setError(null);
    try {
      const template = await downloadScalExcelTemplate(format);
      const url = window.URL.createObjectURL(template);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        format === 'per_sample_tabs'
          ? 'scal_batch_import_per_sample_tabs_template.xlsx'
          : 'scal_batch_import_table_template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to download SCAL Excel template.'
      );
    } finally {
      setDownloadingTemplate(null);
    }
  };

  const handleRemoveInvalidSamples = () => {
    if (!parsedPayload) return;

    const nextFiles = parsedPayload.files.map(file => {
      const samples = file.parsed_data.samples ?? [];
      const nextSamples = samples.filter(sampleIsImportable);
      return {
        ...file,
        samples_detected: nextSamples.length,
        samples_valid: nextSamples.length,
        status:
          file.status === 'failed' && nextSamples.length > 0
            ? 'partial_success'
            : file.status,
        parsed_data: {
          ...file.parsed_data,
          samples: nextSamples,
          samples_detected: nextSamples.length,
          samples_valid: nextSamples.length
        }
      };
    });
    const importableCount = countImportableSamples(nextFiles);

    setParsedPayload({
      files: nextFiles,
      summary: {
        ...parsedPayload.summary,
        samples_detected: importableCount,
        samples_valid: importableCount
      }
    });
    setError(null);
  };

  const updateSampleField = (
    fileIndex: number,
    sampleIndex: number,
    field: 'sample_code' | 'depth' | 'porosity' | 'permeability',
    value: string
  ) => {
    setParsedPayload(current => {
      if (!current) return current;

      const nextFiles = current.files.map((file, currentFileIndex) => {
        if (currentFileIndex !== fileIndex) return file;

        const nextSamples = (file.parsed_data.samples ?? []).map(
          (sample, currentSampleIndex) => {
            if (currentSampleIndex !== sampleIndex) return sample;

            let nextSample: ParsedScalSample = sample;
            if (field === 'sample_code') {
              nextSample = { ...sample, sample_code: value };
            } else if (field === 'depth') {
              nextSample = { ...sample, depth: parseNumericInput(value) };
            } else {
              nextSample = {
                ...sample,
                input_sample_values: {
                  ...(sample.input_sample_values ?? {}),
                  [field]: parseNumericInput(value)
                }
              };
            }

            return {
              ...nextSample,
              ...requiredStatusForSample(nextSample)
            };
          }
        );
        const nextSampleData = file.parsed_data.sample_data?.map(
          (sample, currentSampleIndex) => {
            if (currentSampleIndex !== sampleIndex) return sample;
            if (field === 'sample_code')
              return { ...sample, sample_code: value };
            if (field === 'depth')
              return { ...sample, depth: parseNumericInput(value) };
            return {
              ...sample,
              input_sample_values: {
                ...(sample.input_sample_values ?? {}),
                [field]: parseNumericInput(value)
              }
            };
          }
        );
        const samplesValid = nextSamples.filter(sampleIsImportable).length;

        return {
          ...file,
          samples_valid: samplesValid,
          status:
            file.status === 'failed' && samplesValid > 0
              ? 'partial_success'
              : file.status,
          parsed_data: {
            ...file.parsed_data,
            samples: nextSamples,
            sample_data: nextSampleData,
            samples_valid: samplesValid
          }
        };
      });

      return {
        ...current,
        files: nextFiles,
        summary: {
          ...current.summary,
          samples_valid: countImportableSamples(nextFiles)
        }
      };
    });
    setError(null);
  };

  const handleSave = async () => {
    if (!canSave || !parsedPayload || !projectUid) return;
    setSaving(true);
    setError(null);
    try {
      let wellUid = targetWellUid;
      if (isCreatingNewWell) {
        if (!newWellData.name.trim()) {
          throw new Error('Well name is required.');
        }
        const createdWell = await addWell({
          project_uid: projectUid,
          well_name: newWellData.name.trim(),
          color: newWellData.color,
          curves: []
        });
        wellUid = createdWell.uid;
        const nextWells = [
          ...availableWells.filter(well => well.uid !== createdWell.uid),
          createdWell
        ];
        setAvailableWells(nextWells);
        onWellsChange?.(nextWells);
      }

      const savedSamples = await saveImportedScalSamples(
        projectUid,
        wellUid,
        parsedPayload
      );
      await onImported(savedSamples, wellUid);
      setSuccess(
        `${savedSamples.length} imported sample${savedSamples.length === 1 ? '' : 's'} saved.`
      );
      // Release the large parsed payload + File objects now that they're persisted,
      // instead of holding them in memory until the modal is closed.
      resetImportState();
      setSuccess(
        `${savedSamples.length} imported sample${savedSamples.length === 1 ? '' : 's'} saved.`
      );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to save imported SCAL samples.'
      );
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button
        variant="secondary"
        onClick={onClose}
        disabled={parsing || saving}
      >
        {success ? 'Close' : 'Cancel'}
      </Button>
      {!success && (
        <>
          <Button
            variant="secondary"
            loading={parsing}
            disabled={!canParse}
            onClick={handleParse}
          >
            Parse Files
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!canSave}
            onClick={handleSave}
          >
            Save Imported Samples
          </Button>
        </>
      )}
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Batch Import"
      size="lg"
      footer={footer}
      className={styles.wideModal}
    >
      <div className={styles.content}>
        <p className={styles.description}>
          Upload one or more Excel files containing SCAL sample data. Files are
          parsed first so you can review detected samples, warnings, and errors
          before saving.
        </p>

        <div className={styles.templateCallout}>
          <div>
            <h3 className={styles.sectionTitle}>Excel Template</h3>
            <p className={styles.templateDescription}>
              Use this template if you want to format your SCAL data before
              importing.
            </p>
          </div>
          <div className={styles.templateActions}>
            <Button
              variant="secondary"
              loading={downloadingTemplate === 'table'}
              disabled={Boolean(downloadingTemplate) || parsing || saving}
              onClick={() => handleDownloadTemplate('table')}
            >
              Download Table Template
            </Button>
            <Button
              variant="secondary"
              loading={downloadingTemplate === 'per_sample_tabs'}
              disabled={Boolean(downloadingTemplate) || parsing || saving}
              onClick={() => handleDownloadTemplate('per_sample_tabs')}
            >
              Download Per-Sample Tabs Template
            </Button>
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Files</h3>
          <input
            aria-label="SCAL Excel files"
            className={styles.fileInput}
            type="file"
            accept=".xls,.xlsx"
            multiple
            disabled={parsing || saving}
            onChange={handleFileChange}
          />
          {selectedFiles.length > 0 && (
            <ul className={styles.fileList} aria-label="Selected files">
              {selectedFiles.map(file => (
                <li
                  key={`${file.name}-${file.size}`}
                  className={styles.fileChip}
                >
                  {file.name}
                </li>
              ))}
            </ul>
          )}
          {validationMessage && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              {validationMessage}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Target Well</h3>
          <div className={styles.wellSelect}>
            <div className={styles.comboboxWrapper}>
              <Combobox
                placeholder="Select or create a well"
                value={isCreatingNewWell ? 'NEW_WELL' : targetWellUid}
                disabled={isCreatingNewWell || parsing || saving}
                onChange={value => {
                  setTargetWellUid(value);
                  setIsCreatingNewWell(false);
                }}
                onCreate={name => {
                  setNewWellData(prev => ({ ...prev, name }));
                  setIsCreatingNewWell(true);
                  setTargetWellUid('');
                }}
                options={
                  isCreatingNewWell
                    ? [
                        ...wellOptions,
                        {
                          value: 'NEW_WELL',
                          label: newWellData.name || 'New well'
                        }
                      ]
                    : wellOptions
                }
                fullWidth
              />
              <Button
                variant="secondary"
                onClick={() => {
                  setNewWellData(EMPTY_NEW_WELL);
                  setIsCreatingNewWell(true);
                  setTargetWellUid('');
                }}
                disabled={isCreatingNewWell || parsing || saving}
                title="Create new well"
                aria-label="Create new well"
              >
                <PlusIcon size={16} />
              </Button>
            </div>
          </div>
          {isCreatingNewWell && (
            <div className={styles.newWellSubForm}>
              <div className={styles.newWellSubFormHeader}>
                <span className={styles.newWellSubFormTitle}>
                  New Well Details
                </span>
                <button
                  type="button"
                  className={styles.newWellSubFormCancel}
                  onClick={() => {
                    setIsCreatingNewWell(false);
                    setNewWellData(EMPTY_NEW_WELL);
                  }}
                  title="Cancel new well creation"
                >
                  <XIcon size={18} />
                </button>
              </div>

              <div className={styles.newSampleField}>
                <label
                  className={styles.newSampleLabel}
                  htmlFor="import-new-well-name"
                >
                  Well Name
                </label>
                <TextField
                  id="import-new-well-name"
                  fullWidth
                  value={newWellData.name}
                  onChange={event =>
                    setNewWellData(prev => ({
                      ...prev,
                      name: event.target.value
                    }))
                  }
                />
              </div>

              <div className={styles.newSampleRow}>
                <div className={styles.newSampleField}>
                  <label className={styles.newSampleLabel}>Color</label>
                  <div className={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      className={styles.colorPicker}
                      value={newWellData.color}
                      onChange={event =>
                        setNewWellData(prev => ({
                          ...prev,
                          color: event.target.value
                        }))
                      }
                    />
                    <span className={styles.colorValue}>
                      {newWellData.color}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!hasTargetWell && parsedPayload && (
            <div className={`${styles.alert} ${styles.alertWarning}`}>
              Select a target well before saving imported samples.
            </div>
          )}
          {hasInvalidSamples && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              One or more detected samples are invalid. Remove invalid samples
              to import only the valid rows from this parse.
              <div className={styles.alertActions}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleRemoveInvalidSamples}
                  disabled={parsing || saving}
                >
                  Remove Invalid Samples
                </Button>
              </div>
            </div>
          )}
          {sampleCodeErrors.length > 0 && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              Fix invalid sample codes before saving. Sample codes are required
              and must be {SAMPLE_CODE_MAX_LENGTH} characters or fewer.
            </div>
          )}
          {requiredValueErrors.length > 0 && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              Fix invalid depth, porosity, or permeability values before saving.
              These fields are required and must be valid numbers.
            </div>
          )}
        </div>

        {error && (
          <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
        )}
        {success && (
          <div className={`${styles.alert} ${styles.alertSuccess}`}>
            {success}
          </div>
        )}

        {parsedPayload && (
          <>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Import Summary</h3>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>
                    {parsedPayload.summary.files_processed}
                  </span>
                  <span className={styles.summaryLabel}>Files processed</span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>
                    {parsedPayload.summary.files_failed}
                  </span>
                  <span className={styles.summaryLabel}>Files failed</span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>
                    {parsedPayload.summary.samples_detected}
                  </span>
                  <span className={styles.summaryLabel}>Samples detected</span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>
                    {parsedPayload.summary.samples_valid}
                  </span>
                  <span className={styles.summaryLabel}>Valid samples</span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>
                    {warningMessages.length}
                  </span>
                  <span className={styles.summaryLabel}>Warnings</span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>
                    {errorMessages.length}
                  </span>
                  <span className={styles.summaryLabel}>Errors</span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>File Results</h3>
              {parsedPayload.files.map(file => (
                <div key={file.filename} className={styles.fileResult}>
                  <div className={styles.fileResultHeader}>
                    <span className={styles.fileName}>{file.filename}</span>
                    <span
                      className={`${styles.badge} ${statusClass(file.status)}`}
                    >
                      {statusLabel(file.status)}
                    </span>
                  </div>
                  <div className={styles.fileMeta}>
                    {file.samples_detected} detected · {file.samples_valid}{' '}
                    valid
                  </div>
                  <div className={styles.sectionBadges}>
                    {(
                      file.parsed_data.diagnostics?.sections_detected ?? []
                    ).map(section => (
                      <span
                        key={section}
                        className={`${styles.badge} ${styles.badgeSuccess}`}
                      >
                        {section}
                      </span>
                    ))}
                    {(
                      file.parsed_data.diagnostics?.missing_optional_sections ??
                      []
                    ).map(section => (
                      <span
                        key={section}
                        className={`${styles.badge} ${styles.badgePartial}`}
                      >
                        Missing {section}
                      </span>
                    ))}
                  </div>
                  {file.warnings.length > 0 && (
                    <ul
                      className={`${styles.messageList} ${styles.alertWarning}`}
                    >
                      {file.warnings.map(message => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  )}
                  {file.errors.length > 0 && (
                    <ul
                      className={`${styles.messageList} ${styles.alertError}`}
                    >
                      {file.errors.map(message => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {warningMessages.length > 0 && (
              <div className={`${styles.alert} ${styles.alertWarning}`}>
                <strong>Warnings</strong>
                <ul className={styles.messageList}>
                  {warningMessages.map(message => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            {errorMessages.length > 0 && (
              <div className={`${styles.alert} ${styles.alertError}`}>
                <strong>Errors</strong>
                <ul className={styles.messageList}>
                  {errorMessages.map(message => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            <DataGrid<PreviewSampleRow>
              className={styles.detectedSamplesGrid}
              title="Detected Samples"
              data={sampleRows}
              rowKey="id"
              emptyMessage="No samples detected."
              columns={[
                {
                  key: 'sampleCode',
                  header: 'Sample Code',
                  minWidth: '220px',
                  getValue: row => {
                    const errorMessage = sampleCodeError(row.sampleCode);
                    return (
                      <TextField
                        aria-label={`Sample code for ${row.filename} row ${row.sampleIndex + 1}`}
                        className={styles.sampleCodeInput}
                        size="sm"
                        value={row.sampleCode}
                        status={errorMessage ? 'error' : 'default'}
                        hint={errorMessage ?? undefined}
                        disabled={saving || parsing}
                        onChange={event =>
                          updateSampleField(
                            row.fileIndex,
                            row.sampleIndex,
                            'sample_code',
                            event.target.value
                          )
                        }
                      />
                    );
                  }
                },
                {
                  key: 'depth',
                  header: 'Depth',
                  minWidth: '140px',
                  getValue: row => {
                    const errorMessage = numericFieldError('Depth', row.depth);
                    return (
                      <TextField
                        aria-label={`Depth for ${row.filename} row ${row.sampleIndex + 1}`}
                        className={styles.numericInput}
                        size="sm"
                        type="number"
                        step="any"
                        value={row.depth}
                        status={errorMessage ? 'error' : 'default'}
                        hint={errorMessage ?? undefined}
                        disabled={saving || parsing}
                        onChange={event =>
                          updateSampleField(
                            row.fileIndex,
                            row.sampleIndex,
                            'depth',
                            event.target.value
                          )
                        }
                      />
                    );
                  }
                },
                {
                  key: 'porosity',
                  header: 'Porosity',
                  minWidth: '140px',
                  getValue: row => {
                    const errorMessage = numericFieldError(
                      'Porosity',
                      row.porosity
                    );
                    return (
                      <TextField
                        aria-label={`Porosity for ${row.filename} row ${row.sampleIndex + 1}`}
                        className={styles.numericInput}
                        size="sm"
                        type="number"
                        step="any"
                        value={row.porosity}
                        status={errorMessage ? 'error' : 'default'}
                        hint={errorMessage ?? undefined}
                        disabled={saving || parsing}
                        onChange={event =>
                          updateSampleField(
                            row.fileIndex,
                            row.sampleIndex,
                            'porosity',
                            event.target.value
                          )
                        }
                      />
                    );
                  }
                },
                {
                  key: 'permeability',
                  header: 'Permeability',
                  minWidth: '150px',
                  getValue: row => {
                    const errorMessage = numericFieldError(
                      'Permeability',
                      row.permeability
                    );
                    return (
                      <TextField
                        aria-label={`Permeability for ${row.filename} row ${row.sampleIndex + 1}`}
                        className={styles.numericInput}
                        size="sm"
                        type="number"
                        step="any"
                        value={row.permeability}
                        status={errorMessage ? 'error' : 'default'}
                        hint={errorMessage ?? undefined}
                        disabled={saving || parsing}
                        onChange={event =>
                          updateSampleField(
                            row.fileIndex,
                            row.sampleIndex,
                            'permeability',
                            event.target.value
                          )
                        }
                      />
                    );
                  }
                },
                {
                  key: 'curves',
                  header: 'Curves Detected',
                  minWidth: '240px',
                  getValue: row => (
                    <span className={styles.curvesCell}>
                      {row.curves.length > 0
                        ? row.curves.map(curve => (
                            <span
                              key={curve}
                              className={`${styles.badge} ${styles.badgeNeutral}`}
                            >
                              {curve}
                            </span>
                          ))
                        : '—'}
                    </span>
                  )
                },
                {
                  key: 'sectionsDetected',
                  header: 'Sections',
                  minWidth: '260px',
                  getValue: row => (
                    <span className={styles.curvesCell}>
                      {SECTION_LABELS.map(section => {
                        const detected = row.sectionsDetected.includes(section);
                        const missingOptional =
                          row.missingOptionalSections.includes(section);
                        return (
                          <span
                            key={section}
                            className={`${styles.badge} ${
                              detected
                                ? styles.badgeSuccess
                                : missingOptional
                                  ? styles.badgePartial
                                  : styles.badgeNeutral
                            }`}
                          >
                            {detected ? section : `Missing ${section}`}
                          </span>
                        );
                      })}
                    </span>
                  )
                },
                {
                  key: 'missingRequiredFields',
                  header: 'Required Fields',
                  minWidth: '180px',
                  getValue: row => (
                    <span className={styles.curvesCell}>
                      {row.missingRequiredFields.length > 0 ? (
                        row.missingRequiredFields.map(field => (
                          <span
                            key={field}
                            className={`${styles.badge} ${styles.badgeInvalid}`}
                          >
                            Missing {field}
                          </span>
                        ))
                      ) : (
                        <span
                          className={`${styles.badge} ${styles.badgeSuccess}`}
                        >
                          Complete
                        </span>
                      )}
                    </span>
                  )
                },
                {
                  key: 'status',
                  header: 'Status',
                  minWidth: '100px',
                  getValue: row => (
                    <span
                      className={`${styles.badge} ${statusClass(row.status)}`}
                    >
                      {row.status}
                    </span>
                  )
                }
              ]}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

export default ScalExcelImportModal;
